import type { StaffEngagementSummary } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  EmptyState,
  PageHeader,
  StatusBadge,
} from '@fruuit/ui'
import { Handshake } from 'lucide-react'
import { Link } from 'react-router'
import {
  EngagementFilters,
  useEngagementFilters,
} from '../../features/engagements/EngagementFilters'
import { plural, shortDate, themeLabel } from '../../features/engagements/format'
import { useStaffEngagements } from '../../features/engagements/queries'
import { ProgressBar } from '../../features/engagements/StageTrack'
import { useCursorPager } from '../../features/engagements/useCursorPager'
import { useTitle } from '../../lib/useTitle'

const columns: Column<StaffEngagementSummary>[] = [
  {
    id: 'company',
    header: 'Company / case',
    cell: (row) => (
      <div className="min-w-0 max-w-sm">
        <p className="text-xs font-semibold text-sage">{row.issuer.name}</p>
        <Link
          to={`/workspace/engagements/${row.id}`}
          className="font-semibold text-teal underline-offset-4 hover:underline"
        >
          {row.title}
        </Link>
        <p className="mt-0.5 text-xs text-sage">
          {themeLabel(row.theme)} · {plural(row.progress.objectives, 'objective')}
        </p>
      </div>
    ),
  },
  {
    id: 'team',
    header: 'Case team',
    cell: (row) => (
      <div className="text-xs leading-relaxed text-ink-soft">
        <p>{row.manager.name}</p>
        <p className="text-sage">Reviewer {row.reviewer.name}</p>
      </div>
    ),
  },
  {
    id: 'progress',
    header: 'Evidence progress',
    cell: (row) => (
      <div className="min-w-32">
        <ProgressBar percent={row.progress.percent} label={`Evidence progress for ${row.title}`} />
        <p className="mt-1.5 text-xs text-sage">
          {row.progress.evidenced}/{row.progress.objectives} implementation evidenced
        </p>
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    id: 'release',
    header: 'Investor release',
    cell: (row) => (
      <div className="flex flex-col items-start gap-1">
        {row.released ? (
          <StatusBadge status="published" label="Released" />
        ) : (
          <StatusBadge status="not released" label="Not released" />
        )}
        <span className="text-xs text-sage">
          {row.client_org
            ? `${row.client_org.name} only`
            : `${plural(row.subscriber_count, 'subscriber')}`}
        </span>
      </div>
    ),
  },
  {
    id: 'next',
    header: 'Next action',
    cell: (row) => (
      <div className="min-w-0 max-w-xs">
        <p className="text-sm text-ink">{row.next_action}</p>
        <p className="mt-0.5 text-xs text-sage">Due {shortDate(row.due_date)}</p>
      </div>
    ),
  },
]

/** Workspace engagements: every case, every state, with release and subscriber status. */
export function EngagementsPage() {
  useTitle('Engagements', 'Workspace')
  const { values, update } = useEngagementFilters()
  const pager = useCursorPager(JSON.stringify(values))
  const list = useStaffEngagements({ ...values, ...(pager.cursor ? { cursor: pager.cursor } : {}) })
  const rows = list.data?.items ?? []
  const filtered = Object.values(values).some(Boolean)

  return (
    <>
      <PageHeader
        eyebrow="Engagement programme"
        title="Every case, every state."
        description="Working fields, release status and subscribers for each engagement. Investors only see what has been released."
      />
      <section className="rounded-lg border border-line bg-surface p-4 shadow-[0_1px_2px_rgb(22_62_58/0.04)] sm:p-6">
        <div className="mb-5 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-ink">
              {list.isPending ? 'Engagement cases' : plural(rows.length, 'engagement case')}
            </h2>
            <Badge tone="outline">Team view</Badge>
          </div>
          <EngagementFilters values={values} onChange={update} />
        </div>
        {list.isError ? (
          <Callout tone="danger" role="alert">
            {list.error.message}
          </Callout>
        ) : (
          <DataTable
            caption="Engagement cases (team view)"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            loading={list.isPending}
            empty={
              <EmptyState
                variant="plain"
                icon={<Handshake />}
                title={filtered ? 'No records match this search.' : 'No engagement cases yet.'}
                action={
                  filtered ? (
                    <Button variant="secondary" onClick={() => update({})}>
                      Reset filters
                    </Button>
                  ) : undefined
                }
              />
            }
            {...(list.data?.next_cursor || pager.hasPrevious
              ? {
                  pagination: {
                    mode: 'server' as const,
                    hasNext: Boolean(list.data?.next_cursor),
                    hasPrevious: pager.hasPrevious,
                    onNext: () => pager.next(list.data?.next_cursor),
                    onPrevious: pager.previous,
                    summary: `Page ${pager.page}`,
                  },
                }
              : {})}
          />
        )}
      </section>
    </>
  )
}
